// src/hooks/useDocumentTypeInputs.ts

import { useState } from "react";
import { DocumentTypeInput } from "../types/documentTypes";
import { useAuthFetch } from "../../../utils/useAuthFetch";

export const useCreateDocumentTypeInput = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const createInputField = async (
    documentTypeId: number,
    inputData: Omit<
      DocumentTypeInput,
      "InputFieldID" | "DocumentTypeID" | "CreatedDate"
    >,
  ) => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/inputs?workspace_id=${workspace_id}`,
        "create input field",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(inputData),
        },
      );
      return response;
    } catch (err: any) {
      setError(err.message || "Error creating input field.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createInputField, loading, error };
};

export const useUpdateDocumentTypeInput = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const updateInputField = async (
    documentTypeId: number,
    inputFieldId: number,
    updates: Partial<DocumentTypeInput>,
  ) => {
    try {
      setLoading(true);
      await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/inputs/${inputFieldId}?workspace_id=${workspace_id}`,
        "update input field",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        },
      );
    } catch (err: any) {
      setError(err.message || "Error updating input field.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateInputField, loading, error };
};

export const useDeleteDocumentTypeInput = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const deleteInputField = async (
    documentTypeId: number,
    inputFieldId: number,
  ) => {
    try {
      setLoading(true);
      await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/inputs/${inputFieldId}?workspace_id=${workspace_id}`,
        "delete input field",
        {
          method: "DELETE",
        },
      );
    } catch (err: any) {
      setError(err.message || "Error deleting input field.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { deleteInputField, loading, error };
};

export const useDocumentTypeInputs = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const getInputFields = async (
    documentTypeId: number,
  ): Promise<DocumentTypeInput[]> => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/inputs?workspace_id=${encodeURIComponent(workspace_id)}`,
        "fetch input fields",
      );
      return response;
    } catch (err: any) {
      setError(err.message || "Failed to fetch input fields");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    getInputFields,
    loading,
    error,
  };
};
